"use client";

import { c as _c } from "react/compiler-runtime";
import MediaDisplay from "@/components/drops/view/item/content/media/MediaDisplay";
import VotingModalButton from "@/components/voting/VotingModalButton";
import DropCurationButton from "@/components/waves/drops/DropCurationButton";
import WaveDropActionsOpen from "@/components/waves/drops/WaveDropActionsOpen";
import WaveDropPartContentMarkdown from "@/components/waves/drops/WaveDropPartContentMarkdown";
import { LinkPreviewProvider } from "@/components/waves/LinkPreviewContext";
import { ImageScale } from "@/helpers/image.helpers";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { getDropPreviewImageUrl } from "@/helpers/waves/drop.helpers";
import React, { useCallback, useMemo, useState, useSyncExternalStore } from "react";
interface WaveLeaderboardGridItemViewportProps {
  readonly drop: ExtendedDrop;
  readonly isCompactMode: boolean;
  readonly isContentOnlyMode: boolean;
  readonly showDesktopContentOnlyActions: boolean;
  readonly canOpenDrop: boolean;
  readonly isCuratable: boolean;
  readonly isCurated: boolean;
  readonly canShowVotingAction: boolean;
  readonly onOpenDrop: () => void;
  readonly onVoteButtonClick: () => void;
}
const getGridViewportClassName = ({
  hasMedia,
  isCompactMode
}: {
  readonly hasMedia: boolean;
  readonly isCompactMode: boolean;
}): string => isCompactMode || hasMedia ? "tw-relative tw-overflow-hidden tw-bg-iron-950" : "tw-relative tw-max-h-[20rem] tw-overflow-hidden tw-bg-iron-950";
const getGridContentSpacingClassName = (isCompactMode: boolean): string => isCompactMode ? "tw-space-y-3" : "tw-space-y-1";
const getGridMediaWrapperClassName = (isCompactMode: boolean): string => `tw-relative tw-flex tw-aspect-square tw-min-h-[14rem] tw-items-center tw-justify-center tw-overflow-hidden md:tw-min-h-[15rem] ${isCompactMode ? "tw-rounded-lg tw-bg-iron-900" : "tw-bg-iron-950"}`;
const getGridTextWrapperClassName = ({
  hasMedia,
  isCompactMode
}: {
  readonly hasMedia: boolean;
  readonly isCompactMode: boolean;
}): string => `tw-px-3 ${hasMedia ? "tw-pt-2" : "tw-pt-3"} ${isCompactMode ? "tw-pb-4" : "tw-pb-3"}`;
const getCompactTextViewportClassName = ({
  hasMedia,
  isCompactMode
}: {
  readonly hasMedia: boolean;
  readonly isCompactMode: boolean;
}): string | undefined => {
  if (!isCompactMode) {
    return undefined;
  }
  if (hasMedia) {
    return "tw-max-h-28 tw-overflow-hidden [&_p]:tw-whitespace-normal";
  }
  return "tw-relative tw-max-h-56 tw-overflow-hidden [&_p]:tw-whitespace-normal";
};
function useOverflowGradient(t0) {
  const $ = _c(6);
  const {
    viewportEl,
    innerEl
  } = t0;
  let t1;
  if ($[0] !== innerEl || $[1] !== viewportEl) {
    t1 = () => {
      if (!viewportEl || !innerEl) {
        return false;
      }
      return innerEl.scrollHeight > viewportEl.clientHeight + 1;
    };
    $[0] = innerEl;
    $[1] = viewportEl;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const getOverflowSnapshot = t1;
  let t2;
  if ($[3] !== innerEl || $[4] !== viewportEl) {
    t2 = onStoreChange => {
      if (!viewportEl || !innerEl) {
        return _temp;
      }
      if (typeof ResizeObserver === "undefined") {
        globalThis.addEventListener("resize", onStoreChange);
        return () => {
          globalThis.removeEventListener("resize", onStoreChange);
        };
      }
      const observer = new ResizeObserver(() => {
        onStoreChange();
      });
      observer.observe(viewportEl);
      observer.observe(innerEl);
      return () => {
        observer.disconnect();
      };
    };
    $[3] = innerEl;
    $[4] = viewportEl;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const subscribeToOverflow = t2;
  return useSyncExternalStore(subscribeToOverflow, getOverflowSnapshot, _temp2);
}
function _temp2() {
  return false;
}
function _temp() {}
export const WaveLeaderboardGridItemViewport: React.FC<WaveLeaderboardGridItemViewportProps> = t0 => {
  const $ = _c(55);
  const {
    drop,
    isCompactMode,
    isContentOnlyMode,
    showDesktopContentOnlyActions,
    canOpenDrop,
    isCuratable,
    isCurated,
    canShowVotingAction,
    onOpenDrop,
    onVoteButtonClick
  } = t0;
  const activePart = drop.parts[0];
  const primaryMedia = activePart?.media[0];
  const [viewportEl, setViewportEl] = useState(null);
  const [innerEl, setInnerEl] = useState(null);
  const [compactTextViewportEl, setCompactTextViewportEl] = useState(null);
  const [compactTextInnerEl, setCompactTextInnerEl] = useState(null);
  let t1;
  if ($[0] !== drop.metadata) {
    t1 = getDropPreviewImageUrl(drop.metadata);
    $[0] = drop.metadata;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const previewImageUrl = t1;
  const mediaUrl = primaryMedia?.url ?? null;
  const mediaMimeType = primaryMedia?.mime_type ?? "image/jpeg";
  let t2;
  if ($[2] !== mediaMimeType || $[3] !== previewImageUrl) {
    t2 = mediaMimeType.includes("image") ? null : previewImageUrl;
    $[2] = mediaMimeType;
    $[3] = previewImageUrl;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const mediaPreviewImageUrl = t2;
  const hasMedia = mediaUrl !== null && mediaUrl.length > 0;
  const t3 = activePart?.content ?? "";
  let t4;
  if ($[5] !== t3) {
    t4 = t3.trim();
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const hasTextContent = t4.length > 0;
  const shouldRenderMarkdown = !(isContentOnlyMode && hasMedia);
  const shouldMeasureCompactTextOverflow = isCompactMode && !hasMedia;
  const showCompactMediaReadAction = isCompactMode && hasMedia && hasTextContent && shouldRenderMarkdown;
  let t5;
  if ($[7] !== innerEl || $[8] !== viewportEl) {
    t5 = {
      viewportEl,
      innerEl
    };
    $[7] = innerEl;
    $[8] = viewportEl;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const showGradient = useOverflowGradient(t5);
  let t6;
  if ($[10] !== compactTextInnerEl || $[11] !== compactTextViewportEl) {
    t6 = {
      viewportEl: compactTextViewportEl,
      innerEl: compactTextInnerEl
    };
    $[10] = compactTextInnerEl;
    $[11] = compactTextViewportEl;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const showCompactTextGradient = useOverflowGradient(t6);
  let t7;
  if ($[13] !== onOpenDrop) {
    t7 = event => {
      event.stopPropagation();
      onOpenDrop();
    };
    $[13] = onOpenDrop;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const onReadFullTextClick = t7;
  let t8;
  if ($[15] !== hasMedia || $[16] !== isCompactMode) {
    t8 = getGridViewportClassName({
      hasMedia,
      isCompactMode
    });
    $[15] = hasMedia;
    $[16] = isCompactMode;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== isCompactMode) {
    t9 = getGridContentSpacingClassName(isCompactMode);
    $[18] = isCompactMode;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== hasMedia || $[21] !== isCompactMode || $[22] !== mediaMimeType || $[23] !== mediaPreviewImageUrl || $[24] !== mediaUrl) {
    t10 = hasMedia && <div className={getGridMediaWrapperClassName(isCompactMode)}><MediaDisplay media_mime_type={mediaMimeType} media_url={mediaUrl} disableMediaInteraction={true} imageScale={ImageScale.AUTOx450} previewImageUrl={mediaPreviewImageUrl} /></div>;
    $[20] = hasMedia;
    $[21] = isCompactMode;
    $[22] = mediaMimeType;
    $[23] = mediaPreviewImageUrl;
    $[24] = mediaUrl;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== activePart || $[27] !== drop || $[28] !== hasMedia || $[29] !== isCompactMode || $[30] !== onReadFullTextClick || $[31] !== shouldMeasureCompactTextOverflow || $[32] !== shouldRenderMarkdown || $[33] !== showCompactMediaReadAction || $[34] !== showCompactTextGradient) {
    t11 = activePart && shouldRenderMarkdown && <div className={getGridTextWrapperClassName({
      hasMedia,
      isCompactMode
    })}><div ref={shouldMeasureCompactTextOverflow ? setCompactTextViewportEl : undefined} className={getCompactTextViewportClassName({
        hasMedia,
        isCompactMode
      })}><div ref={shouldMeasureCompactTextOverflow ? setCompactTextInnerEl : undefined}><LinkPreviewProvider variant="home"><WaveDropPartContentMarkdown mentionedUsers={drop.mentioned_users} mentionedGroups={drop.mentioned_groups} mentionedWaves={drop.mentioned_waves} referencedNfts={drop.referenced_nfts} part={activePart} wave={drop.wave} drop={drop} onQuoteClick={_temp3} /></LinkPreviewProvider></div>{shouldMeasureCompactTextOverflow && showCompactTextGradient && <div className="tw-pointer-events-none tw-absolute tw-inset-x-0 tw-bottom-0 tw-h-14 tw-bg-gradient-to-t tw-from-iron-950 tw-via-iron-950/70 tw-to-transparent" />}</div>{showCompactMediaReadAction && <button type="button" onClick={onReadFullTextClick} className="desktop-hover:hover:tw-text-primary-200 tw-mt-2 tw-inline-flex tw-items-center tw-border-0 tw-bg-transparent tw-p-0 tw-text-xs tw-font-semibold tw-text-primary-300 tw-underline-offset-2 tw-transition-colors focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 focus-visible:tw-outline-primary-400 desktop-hover:hover:tw-underline">Read full text</button>}</div>;
    $[26] = activePart;
    $[27] = drop;
    $[28] = hasMedia;
    $[29] = isCompactMode;
    $[30] = onReadFullTextClick;
    $[31] = shouldMeasureCompactTextOverflow;
    $[32] = shouldRenderMarkdown;
    $[33] = showCompactMediaReadAction;
    $[34] = showCompactTextGradient;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  let t12;
  if ($[36] !== t10 || $[37] !== t11 || $[38] !== t9) {
    t12 = <div ref={setInnerEl} className={t9}>{t10}{t11}</div>;
    $[36] = t10;
    $[37] = t11;
    $[38] = t9;
    $[39] = t12;
  } else {
    t12 = $[39];
  }
  let t13;
  if ($[40] !== showGradient) {
    t13 = showGradient && <div className="tw-pointer-events-none tw-absolute tw-inset-x-0 tw-bottom-0 tw-h-14 tw-bg-gradient-to-t tw-from-iron-900 tw-via-iron-900/90 tw-to-transparent" />;
    $[40] = showGradient;
    $[41] = t13;
  } else {
    t13 = $[41];
  }
  let t14;
  if ($[42] !== canOpenDrop || $[43] !== canShowVotingAction || $[44] !== drop || $[45] !== isCuratable || $[46] !== isCurated || $[47] !== onVoteButtonClick || $[48] !== showDesktopContentOnlyActions) {
    t14 = showDesktopContentOnlyActions && <div data-testid={`wave-leaderboard-grid-item-content-only-actions-${drop.id}`} className="tw-pointer-events-none tw-absolute tw-inset-x-0 tw-bottom-0 tw-z-0 tw-bg-gradient-to-t tw-from-black/90 tw-via-black/65 tw-to-transparent tw-p-2 tw-opacity-0 tw-transition-opacity tw-duration-200 group-focus-within:tw-opacity-100 desktop-hover:group-hover:tw-opacity-100"><div className="tw-pointer-events-auto tw-flex tw-flex-wrap tw-items-center tw-justify-end tw-gap-2">{canOpenDrop && <div className="tw-flex tw-h-8 tw-items-center"><WaveDropActionsOpen drop={drop} /></div>}{isCuratable && <DropCurationButton dropId={drop.id} waveId={drop.wave.id} isCuratable={isCuratable} isCurated={isCurated} className="tw-bg-iron-950/70" />}{canShowVotingAction && <VotingModalButton drop={drop} onClick={onVoteButtonClick} variant="subtle" />}</div></div>;
    $[42] = canOpenDrop;
    $[43] = canShowVotingAction;
    $[44] = drop;
    $[45] = isCuratable;
    $[46] = isCurated;
    $[47] = onVoteButtonClick;
    $[48] = showDesktopContentOnlyActions;
    $[49] = t14;
  } else {
    t14 = $[49];
  }
  let t15;
  if ($[50] !== t12 || $[51] !== t13 || $[52] !== t14 || $[53] !== t8) {
    t15 = <div ref={setViewportEl} className={t8}>{t12}{t13}{t14}</div>;
    $[50] = t12;
    $[51] = t13;
    $[52] = t14;
    $[53] = t8;
    $[54] = t15;
  } else {
    t15 = $[54];
  }
  return t15;
};
function _temp3() {}